<script setup lang="ts">
  import { useNotifications } from '@/shared/lib'
  import { EditorPageHeader } from '@/shared/ui/components'
  import PageDecorationImage from '@/shared/ui/components/PageDecorationImage.vue'

  import { EventsEditor, getEvent } from '@/features/events'

  definePage({
    meta: {
      layout: 'baseNew',
      contentManagerRoute: true,
    },
  })

  const { t } = useI18n()
  const route = useRoute('/events/item=[id]/edit/')
  const router = useRouter()
  const { errorNotification } = useNotifications()

  const eventId = computed(() => Number(route.params.id))
  const item = ref<Awaited<ReturnType<typeof getEvent>> | null>(null)
  const isPending = ref(true)
  const error = ref<unknown>(null)

  const loadEvent = async () => {
    isPending.value = true
    error.value = null
    try {
      item.value = await getEvent(eventId.value)
    } catch (loadError) {
      error.value = loadError
    } finally {
      isPending.value = false
    }
  }

  watch(eventId, loadEvent, { immediate: true })

  const breadcrumbLabel = computed(() => {
    if (item.value?.title) {
      return item.value.title
    }
    return t('events_breadcrumb_edit')
  })

  useHead({
    title: () => t('events_edit_page_title'),
  })

  watch(error, async (queryError) => {
    if (!queryError) {
      return
    }

    const status = (queryError as { status?: number }).status

    if (status === 404) {
      errorNotification('events_item_not_found')
    } else {
      errorNotification('base_something_went_wrong')
    }

    await router.push({ name: '/events/[[page]]' })
  })
</script>

<template>
  <div class="wide-responsive relative flex w-full flex-col">
    <PageDecorationImage class="pointer-events-none absolute top-0 right-0" />

    <EditorPageHeader
      parent-route-name="/events/[[page]]"
      parent-label-key="navigation_item_announcements"
      :current-label="breadcrumbLabel"
      data-testid="events-editor-page-header"
    />
    <div
      v-if="isPending"
      class="flex min-h-40 items-center justify-center py-8"
      data-testid="events-edit-loading"
      aria-live="polite"
      aria-busy="true"
    >
      <NSpin size="medium" />
    </div>
    <EventsEditor
      v-else-if="item"
      :item="item"
    />
  </div>
</template>
